package utils

import kotlin.js.Promise
import utils.stringifyJson

/**
 * Web Push API Manager for browser push notifications
 *
 * Example usage:
 * ```kotlin
 * val pushManager = WebPushManager("http://localhost:3001")
 * pushManager.subscribe { result ->
 *     if (result.isSuccess) {
 *         println("Subscribed to push notifications")
 *     } else {
 *         println("Failed to subscribe: ${result.exceptionOrNull()}")
 *     }
 * }
 * ```
 */

class WebPushManager(private val apiBaseUrl: String) {

    private var registration: ServiceWorkerRegistration? = null
    private var subscription: PushSubscription? = null

    /**
     * Request notification permission and subscribe to push notifications
     *
     * @param onSuccess Callback when subscription is successful
     * @param onError Callback when subscription fails
     */
    fun subscribe(
        onSuccess: (PushSubscriptionJSON) -> Unit,
        onError: (String) -> Unit
    ) {
        // Request notification permission
        val permissionPromise: Promise<String> = Notification.requestPermission()
        permissionPromise.then(
            onFulfilled = { permission ->
                if (permission != "granted") {
                    onError("Notification permission denied")
                } else {
                    proceedWithSubscription(onSuccess, onError)
                }
                undefined
            },
            onRejected = { e ->
                onError("Permission request failed: $e")
                undefined
            }
        )
    }

    private fun proceedWithSubscription(
        onSuccess: (PushSubscriptionJSON) -> Unit,
        onError: (String) -> Unit
    ) {
        val readyPromise = navigator.serviceWorker?.ready
        if (readyPromise == null) {
            onError("Service worker not registered")
            return
        }

        readyPromise.then(
            onFulfilled = { reg ->
                registration = reg
                fetchVapidKeyAndSubscribe(reg, onSuccess, onError)
                undefined
            },
            onRejected = { e ->
                onError("Service worker registration failed: $e")
                undefined
            }
        )
    }

    private fun fetchVapidKeyAndSubscribe(
        reg: ServiceWorkerRegistration,
        onSuccess: (PushSubscriptionJSON) -> Unit,
        onError: (String) -> Unit
    ) {
        val fetchPromise: Promise<Response> = window.fetch("$apiBaseUrl/api/push/vapid-public-key")
        fetchPromise.then(
            onFulfilled = { response ->
                if (!response.ok) {
                    onError("Failed to get VAPID public key")
                } else {
                    response.json().then(
                        onFulfilled = { json: dynamic ->
                            @Suppress("UnsafeCastFromDynamic")
                            val vapidKeyResponse = json as? VapidKeyResponse
                            val vapidPublicKey = vapidKeyResponse?.publicKey

                            if (vapidPublicKey == null) {
                                onError("Invalid VAPID key response")
                            } else {
                                subscribeToPush(reg, vapidPublicKey, onSuccess, onError)
                            }
                            undefined
                        },
                        onRejected = { e: Any? ->
                            onError("Failed to parse VAPID key: $e")
                            undefined
                        }
                    )
                }
                undefined
            },
            onRejected = { e ->
                onError("Failed to fetch VAPID key: $e")
                undefined
            }
        )
    }

    private fun subscribeToPush(
        reg: ServiceWorkerRegistration,
        vapidPublicKey: String,
        onSuccess: (PushSubscriptionJSON) -> Unit,
        onError: (String) -> Unit
    ) {
        val options = js("""
            ({
                userVisibleOnly: true,
                applicationServerKey: vapidPublicKey
            })
        """) as PushSubscriptionOptions

        val subscribePromise: Promise<PushSubscription> = reg.pushManager.subscribe(options)
        subscribePromise.then(
            onFulfilled = { pushSubscription ->
                subscription = pushSubscription
                val subJson = pushSubscription.toJSON()
                sendSubscriptionToServer(subJson, onSuccess, onError)
                undefined
            },
            onRejected = { e ->
                onError("Failed to subscribe to push: $e")
                undefined
            }
        )
    }

    private fun sendSubscriptionToServer(
        subJson: PushSubscriptionJSON,
        onSuccess: (PushSubscriptionJSON) -> Unit,
        onError: (String) -> Unit
    ) {
        val endpoint = subJson.endpoint
        val p256dh = subJson.keys?.p256dh ?: ""
        val auth = subJson.keys?.auth ?: ""
        val url = "$apiBaseUrl/api/push/subscribe"

        // Build payload object and safely serialize to JSON to prevent injection
        val payload = object {
            val endpoint = endpoint
            val keys = object {
                val p256dh = p256dh
                val auth = auth
            }
        }
        val bodyJson = stringifyJson(payload)

        // Using native fetch with safely constructed JSON body
        val sendPromise = window.fetch(
            url,
            js("{method: 'POST', headers: {'Content-Type': 'application/json'}, body: bodyJson}") as RequestInit
        )
        sendPromise.then(
            onFulfilled = {
                onSuccess(subJson)
                undefined
            },
            onRejected = { e ->
                console.error("Failed to send subscription to server:", e)
                onError("Failed to send subscription to server")
                undefined
            }
        )
    }

    /**
     * Unsubscribe from push notifications
     */
    fun unsubscribe(
        onSuccess: () -> Unit,
        onError: (String) -> Unit
    ) {
        val sub = subscription
        if (sub == null) {
            onSuccess()
            return
        }

        val endpoint = sub.endpoint
        val url = "$apiBaseUrl/api/push/unsubscribe"

        // Build payload object and safely serialize to JSON to prevent injection
        val payload = object {
            val endpoint = endpoint
        }
        val bodyJson = stringifyJson(payload)

        // Using native fetch with safely constructed JSON body
        val fetchPromise = window.fetch(
            url,
            js("{method: 'DELETE', headers: {'Content-Type': 'application/json'}, body: bodyJson}") as RequestInit
        )
        fetchPromise.then(
            onFulfilled = {
                // Unsubscribe from push service
                sub.unsubscribe().then(
                    onFulfilled = {
                        subscription = null
                        onSuccess()
                        undefined
                    },
                    onRejected = { e ->
                        console.error("Failed to unsubscribe from push service:", e)
                        onError("Unsubscribe failed: $e")
                        undefined
                    }
                )
                undefined
            },
            onRejected = { e ->
                console.error("Failed to send unsubscribe to server:", e)
                onError("Failed to send unsubscribe to server")
                undefined
            }
        )
    }

    /**
     * Check if push notifications are supported
     */
    fun isSupported(): Boolean {
        return js("('serviceWorker' in navigator) && ('PushManager' in window)") as Boolean
    }

    /**
     * Check if currently subscribed
     */
    fun isSubscribed(): Boolean {
        return subscription != null
    }

    /**
     * Set up push notification event listener
     * Call this from service worker's 'push' event
     */
    fun setupPushNotificationHandler(
        onNotification: (dynamic) -> Unit
    ) {
        // Note: This should be called from service worker context
        // The actual setup needs to be done in sw.js
    }

    /**
     * Show a notification using the service worker registration
     */
    fun showNotification(title: String, options: NotificationOptions?) {
        if (options != null) {
            registration?.showNotification(title, options)
        } else {
            registration?.showNotification(title)
        }
    }
}

// External interfaces and type definitions
external interface VapidKeyResponse {
    val publicKey: String
}

/**
 * Service Worker setup for push notifications
 * Add this to your sw.js file:
 * ```javascript
 * self.addEventListener('push', (event) => {
 *   const data = event.data.json();
 *   self.registration.showNotification(data.title, {
 *     body: data.body,
 *     icon: data.icon,
 *     badge: data.badge,
 *     data: data.data,
 *     tag: data.tag || 'default'
 *   });
 * });
 *
 * self.addEventListener('notificationclick', (event) => {
 *   event.notification.close();
 *   if (event.notification.data) {
 *     const url = event.notification.data.url;
 *     if (url) {
 *       event.waitUntil(clients.openWindow(url));
 *     }
 *   }
 * });
 * ```
 */
